import { Autocomplete, FormGroup, FormHelperText, FormLabel, TextField } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Collaborator, getCollaboratorByName } from "src/apis/company";
import { extractErrorMessage } from "src/utils/http";
import { Id } from "src/utils/types";
import { useDebouncedCallback } from 'use-debounce';

type CollaboratorSelectProps = {
  isFilter?: boolean,
  isOptional?: boolean,
  companyId: Id,
  collaboratorId: Id,
  setCollaborator: (collaborator: Collaborator) => void,
  unsetCollaborator?: (collaborator: Collaborator) => void,
  setError: (err: string) => void,
  initialValue?: Collaborator | null,
  helperText?: string,
  disabled?: boolean,
}

const CollaboratorSelect: FC<CollaboratorSelectProps> = ({
  isFilter = false,
  companyId,
  collaboratorId,
  setCollaborator,
  unsetCollaborator,
  setError,
  initialValue,
  helperText,
  disabled = false,
}): JSX.Element => {
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [name, setName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Descarta respostas de buscas antigas: sem isso uma requisição lenta de
  // "ana" sobrescreve a lista já carregada de "anabela".
  const requestIdRef = useRef<number>(0);
  // Último colaborador aplicado a partir do initialValue. setCollaborator
  // costuma ser uma arrow inline (identidade nova a cada render), então sem
  // esse controle o effect reaplicaria o colaborador anterior indefinidamente.
  const appliedCollaboratorIdRef = useRef<Id>(undefined);
  const previousCompanyIdRef = useRef<Id>(companyId);

  const fetchColaboradores = useCallback(async (name: string) => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    setIsLoading(true);

    try {
      const response = await getCollaboratorByName(name, companyId as number);

      if (requestId !== requestIdRef.current) return;

      setCollaborators(response);
      setError('');
    } catch (err: unknown) {
      if (requestId !== requestIdRef.current) return;

      setError(extractErrorMessage(err, 'Erro ao buscar colaboradores.'));
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [companyId, setError]);

  const debouncedFetchColaboradores = useDebouncedCallback((name: string) => {
    fetchColaboradores(name);
  }, 1000);

  const collaboratorByName = (name: string): Collaborator|undefined => {
    return collaborators?.find((c) => c.nome === name) ?? undefined;
  }

  // Trocar de empresa invalida a busca anterior: manter as opções faria o
  // usuário selecionar um colaborador que não pertence à empresa escolhida.
  useEffect(() => {
    if (previousCompanyIdRef.current === companyId) return;

    previousCompanyIdRef.current = companyId;
    requestIdRef.current += 1;
    appliedCollaboratorIdRef.current = undefined;
    setCollaborators([]);
    setName('');
    setIsLoading(false);
  }, [companyId]);

  useEffect(() => {
    if (!initialValue) {
      // O pai limpou a seleção (ex.: começar um novo cadastro)
      if (appliedCollaboratorIdRef.current !== undefined) {
        appliedCollaboratorIdRef.current = undefined;
        setName('');
      }
      return;
    }

    if (appliedCollaboratorIdRef.current === initialValue.id_colaborador) return;

    appliedCollaboratorIdRef.current = initialValue.id_colaborador;
    setName(initialValue.nome);
    setCollaborator(initialValue);
  }, [initialValue, setCollaborator])

  return (
    <FormGroup>
      {!isFilter && (
        <FormLabel>Colaborador</FormLabel>
      )}

      <Autocomplete
        value={initialValue ?? collaboratorByName(name) ?? null}
        loading={isLoading}
        disabled={!companyId || disabled}
        isOptionEqualToValue={(option, value) => option.id_colaborador === value.id_colaborador}
        getOptionLabel={(option) => option.nome}
        options={collaborators}
        onInputChange={(e, newInputValue, reason) => {
          if (reason === 'clear') {
            appliedCollaboratorIdRef.current = undefined;
            unsetCollaborator?.(collaboratorByName(name)!);
          } else {
            debouncedFetchColaboradores(newInputValue as string);
          }

          setName(newInputValue);
        }}
        onBlur={(e) => {
          const collaborator = collaboratorByName(name);

          if (Boolean(collaborator)) {
            appliedCollaboratorIdRef.current = collaborator!.id_colaborador;
            setCollaborator(collaborator!);
          }
        }}
        renderInput={(params) => {
          const props = isFilter ? {label: 'Colaborador', sx: {'width': '200px'}} : {};

          return (
            <TextField {...params} {...props} />
          )
        }}
      />
      {(!collaboratorId && !isFilter) && (
        <FormHelperText>
          {Boolean(helperText) ? helperText : "Pesquise por colaboradores"}
        </FormHelperText>
      )}
    </FormGroup>
  )
}

export default CollaboratorSelect;
